// Single source of truth for the advanced-search wire format.
//
// A search is a left-to-right sequence of clauses joined by logical operators:
//
//   'Field'=value                     attributed clause (value may be "a,b" = OR)
//   'Field'[op]=value                 pro clause with an explicit comparison
//   bareText                          unattributed clause (free-text LIKE)
//   <clause> && | || | ^^ <clause>    AND / OR / XOR between clauses
//
// Pro clauses are super-admin only and the backend enforces that independently —
// `q` is just a URL param, so nothing here is a security boundary. Sanitizing on
// this side only keeps the wire string from being corrupted structurally.
//
// This mirrors the backend tokenizer in backend/helpers/attributeSearch.js so the
// string produced here parses identically server-side. `&&` is also the legacy
// separator, so pre-existing `q` values keep working.

export type Operator = 'AND' | 'OR' | 'XOR';

export const OPERATOR_WIRE: Record<Operator, string> = {
  AND: '&&',
  OR: '||',
  XOR: '^^',
};

const WIRE_TO_OPERATOR: Record<string, Operator> = {
  '&&': 'AND',
  '||': 'OR',
  '^^': 'XOR',
};

export const OPERATOR_LABELS: Record<Operator, string> = {
  AND: 'AND',
  OR: 'OR',
  XOR: 'XOR',
};

// Comparison operators for "pro" clauses. Mirrors the closed enum in
// backend/helpers/searchOperators.js — keep the two in sync.
export type ComparisonOperator =
  | 'like'
  | 'nlike'
  | 'eq'
  | 'ne'
  | 'gt'
  | 'gte'
  | 'lt'
  | 'lte'
  | 'in'
  | 'nin'
  | 'isnull'
  | 'notnull';

export const COMPARISON_LABELS: Record<ComparisonOperator, string> = {
  like: 'contains',
  nlike: 'does not contain',
  eq: 'equals',
  ne: 'not equals',
  gt: 'greater than',
  gte: 'greater or equal',
  lt: 'less than',
  lte: 'less or equal',
  in: 'is one of',
  nin: 'is none of',
  isnull: 'is empty',
  notnull: 'is not empty',
};

/** Operators taking several comma-separated values. */
export const MULTI_COMPARISONS: ComparisonOperator[] = ['in', 'nin'];
/** Operators taking no value at all. */
export const NULLARY_COMPARISONS: ComparisonOperator[] = ['isnull', 'notnull'];

export const isMultiComparison = (op?: ComparisonOperator) => !!op && MULTI_COMPARISONS.includes(op);
export const isNullaryComparison = (op?: ComparisonOperator) => !!op && NULLARY_COMPARISONS.includes(op);

const COMPARISON_SET = new Set<string>(Object.keys(COMPARISON_LABELS));

const PRO_CLAUSE_PATTERN = /^'([^']+)'\[([a-zA-Z]+)\]=([\s\S]*)$/;

/** True when a single wire fragment is a pro clause (`'field'[op]=value`). */
export function isProClauseString(part: string): boolean {
  const match = part.trim().match(PRO_CLAUSE_PATTERN);
  return !!match && COMPARISON_SET.has(match[2].toLowerCase());
}

export interface ClauseToken {
  type: 'clause';
  /** Field label / wire key. Omitted for an unattributed free-text clause. */
  field?: string;
  /** One value, or several for a multi-select (OR within the field). */
  values: string[];
  /**
   * Set only on pro clauses. Its presence is what switches the wire format to
   * `'field'[op]=value` and what makes the backend demand super admin.
   */
  comparison?: ComparisonOperator;
}

export interface OperatorToken {
  type: 'operator';
  operator: Operator;
}

export type SearchToken = ClauseToken | OperatorToken;

// Characters/sequences that are structural in the grammar and must never appear
// raw inside a value, or they would corrupt tokenization. Stripped defensively on
// the way out; the backend independently re-validates and parameterizes.
const RESERVED = /(\|\||\^\^|&&|[,='])/g;

// Structured (attributed) value: `,` and `=` are meaningful separators, so they
// are stripped along with the operators and quotes.
export function sanitizeValue(value: string): string {
  return String(value ?? '')
    .replace(RESERVED, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

// Free-text (unattributed) clause: only the operator sequences and the quote that
// starts an attributed clause are structural, so commas/equals are preserved.
const RESERVED_FREE_TEXT = /(\|\||\^\^|&&|')/g;

export function sanitizeFreeText(value: string): string {
  return String(value ?? '')
    .replace(RESERVED_FREE_TEXT, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

// Pro-clause value. Only the operator sequences are structural here: the backend's
// pro pattern is anchored (`^'field'[op]=<rest>`), so quotes and `=` inside the
// value cannot break out of it. Commas stay meaningful for `in`/`nin` only, and
// are stripped per-value by the serializer below.
const RESERVED_PRO_VALUE = /(\|\||\^\^|&&)/g;

export function sanitizeProValue(value: string): string {
  return String(value ?? '')
    .replace(RESERVED_PRO_VALUE, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/** Drop leading/trailing operators and any operator not sitting between two clauses. */
export function normalizeTokens(tokens: SearchToken[]): SearchToken[] {
  const out: SearchToken[] = [];
  for (const token of tokens) {
    if (token.type === 'operator') {
      if (out.length === 0 || out[out.length - 1].type === 'operator') continue;
      out.push(token);
    } else {
      out.push(token);
    }
  }
  while (out.length && out[out.length - 1].type === 'operator') out.pop();
  return out;
}

export function serializeTokens(tokens: SearchToken[]): string {
  const parts: string[] = [];
  for (const token of normalizeTokens(tokens)) {
    if (token.type === 'operator') {
      parts.push(OPERATOR_WIRE[token.operator]);
      continue;
    }
    if (!token.field) {
      const text = sanitizeFreeText(token.values[0] ?? '');
      if (text) parts.push(text);
      continue;
    }
    if (token.comparison) {
      if (isNullaryComparison(token.comparison)) {
        parts.push(`'${token.field}'[${token.comparison}]=`);
        continue;
      }
      const proValues = token.values
        .map(sanitizeProValue)
        // A comma would read as a value separator, which only `in`/`nin` want.
        .map(v => (isMultiComparison(token.comparison) ? v.replace(/,/g, ' ').trim() : v))
        .filter(Boolean);
      if (!proValues.length) continue;
      const joined = isMultiComparison(token.comparison) ? proValues.join(',') : proValues[0];
      parts.push(`'${token.field}'[${token.comparison}]=${joined}`);
      continue;
    }
    const values = token.values.map(sanitizeValue).filter(Boolean);
    if (!values.length) continue;
    parts.push(`'${token.field}'=${values.join(',')}`);
  }
  return parts.join(' ').trim();
}

export function parseSearchString(search: string): SearchToken[] {
  if (!search || !search.trim()) return [];

  // Accept the legacy textual " and " synonym so old bookmarks rehydrate.
  const normalized = search.replace(/ and /g, ' && ');
  const rawParts = normalized.split(/(\|\||\^\^|&&)/);
  const tokens: SearchToken[] = [];

  for (const part of rawParts) {
    const trimmed = part.trim();
    if (!trimmed) continue;

    const operator = WIRE_TO_OPERATOR[trimmed];
    if (operator) {
      tokens.push({ type: 'operator', operator });
      continue;
    }

    const pro = trimmed.match(PRO_CLAUSE_PATTERN);
    if (pro && COMPARISON_SET.has(pro[2].toLowerCase())) {
      const comparison = pro[2].toLowerCase() as ComparisonOperator;
      const rest = pro[3].trim();
      const values = isNullaryComparison(comparison)
        ? []
        : isMultiComparison(comparison)
          ? rest
              .split(',')
              .map(v => v.trim())
              .filter(Boolean)
          : [rest].filter(Boolean);
      tokens.push({ type: 'clause', field: pro[1].trim(), values, comparison });
      continue;
    }

    const attributed = trimmed.match(/^'([^']+)'=(.*)$/);
    if (attributed) {
      const field = attributed[1].trim();
      const values = attributed[2]
        .split(',')
        .map(v => v.trim())
        .filter(Boolean);
      tokens.push({ type: 'clause', field, values });
    } else {
      tokens.push({ type: 'clause', values: [trimmed] });
    }
  }

  return normalizeTokens(tokens);
}

/** True when any clause in the wire string is a pro clause. */
export function hasProClause(search: string): boolean {
  return parseSearchString(search).some(token => token.type === 'clause' && !!token.comparison);
}

/** True when the next thing appended may be an operator (i.e. last token is a clause). */
export function canAppendOperator(tokens: SearchToken[]): boolean {
  return tokens.length > 0 && tokens[tokens.length - 1].type === 'clause';
}
